/**
 * 路由级 SEO 元信息的客户端写入器。
 *
 * canonical 与 og:url 在运行时一律取 window.location.origin：本机、局域网 IP、正式域名三种
 * 部署下都自动正确，不必为它配一个 SITE_ORIGIN。SITE_ORIGIN 只在构建期生成
 * sitemap.xml / 预渲染产物时才需要（那时没有浏览器可用）。
 *
 * 这些标签全是不可见的：不动 DOM 结构、不动样式、不动任何事件，
 * 因此不会改变页面行为，也不会与后台「站点信息」下发的 favicon 改写逻辑相互覆盖。
 */
import { canonicalFor, isActivityDetail, matchSeoPage, robotsFor } from './pages';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>("link[rel='canonical']");
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

function removeMeta(attr: 'name' | 'property', key: string) {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

function removeCanonical() {
  document.head.querySelector("link[rel='canonical']")?.remove();
}

/**
 * 工作台的 /customers /orders 等未公开页：把公开页那套标签整组摘掉，而不是留空 content。
 * 用户可能从 / 登录后进工作台，此时若只把描述清空、却留着指向 / 的 canonical 与 og:url，
 * 分享出去的链接预览和抓取端读到的就是「这条应用内地址规范到首页」，比没有标签更容易误导。
 */
function clearPublicMeta() {
  removeMeta('name', 'description');
  removeCanonical();
  for (const key of ['og:title', 'og:description', 'og:type', 'og:url', 'og:site_name']) {
    removeMeta('property', key);
  }
  for (const key of ['twitter:card', 'twitter:title', 'twitter:description']) {
    removeMeta('name', key);
  }
}

/**
 * 浏览器标签标题：命中公开页时「页面名 - 站点名」，其余保持只写站点名（原行为）。
 * 分享页由页面自身定标题，这里不参与。
 */
export function resolveDocumentTitle(pathname: string, siteName: string): string {
  const page = matchSeoPage(pathname);
  return page ? `${page.title} - ${siteName}` : siteName;
}

/** 页面自己提供的元信息（活动落地页用后台数据现填） */
export interface PageMeta {
  title: string;
  description: string;
  robots: 'index,follow' | 'noindex,nofollow';
  /** 规范地址：绝对 URL，页面用 window.location.origin 现拼 */
  canonical: string;
  /** 卡片图（活动主图），没有就不写图片标签 */
  image?: string | null;
  siteName?: string;
}

/**
 * 写入一条页面自持的元信息。
 *
 * 与 applyRouteMeta 一样只动 <head> 里的标签，不碰 DOM 结构与事件；先 clearPublicMeta 再写，
 * 是从 /about 这类带 og:image 之外标签的公开页跳进来时不留残值的唯一办法。
 */
export function applyPageMeta(meta: PageMeta) {
  clearPublicMeta();
  upsertMeta('name', 'robots', meta.robots);
  upsertMeta('name', 'description', meta.description);
  upsertCanonical(meta.canonical);
  upsertMeta('property', 'og:title', meta.title);
  upsertMeta('property', 'og:description', meta.description);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:url', meta.canonical);
  upsertMeta('property', 'og:site_name', meta.siteName || document.title);
  if (meta.image) upsertMeta('property', 'og:image', meta.image);
  upsertMeta('name', 'twitter:card', meta.image ? 'summary_large_image' : 'summary');
  upsertMeta('name', 'twitter:title', meta.title);
  upsertMeta('name', 'twitter:description', meta.description);
  if (meta.image) upsertMeta('name', 'twitter:image', meta.image);
}

/** 写入当前路由的描述、规范地址、收录指令与社交卡片标签 */
export function applyRouteMeta(pathname: string, siteName?: string) {
  // 活动落地页由页面自己按后台数据写（见 pages.ts 的 isActivityDetail）
  if (isActivityDetail(pathname)) return;

  const page = matchSeoPage(pathname);

  // 先摘干净再写：从公开页导航进工作台或分享页时，不能残留上一条路由的规范地址与卡片标签
  clearPublicMeta();
  upsertMeta('name', 'robots', robotsFor(pathname));

  // 分享页带一次性令牌：robots 已是 noindex,nofollow（此前该页完全没有收录指令），不再挂任何指向它的标签
  if (pathname.startsWith('/share/')) return;

  if (!page) return;

  const canonical = `${window.location.origin}${canonicalFor(pathname)}`;
  const ogTitle = page.title;

  upsertMeta('name', 'description', page.description);
  upsertCanonical(canonical);
  upsertMeta('property', 'og:title', ogTitle);
  upsertMeta('property', 'og:description', page.description);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:site_name', siteName || document.title);
  upsertMeta('name', 'twitter:card', 'summary');
  upsertMeta('name', 'twitter:title', ogTitle);
  upsertMeta('name', 'twitter:description', page.description);
}
